import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Trophy, Sparkles, Zap, Plus, Calendar, BarChart3, Loader2, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const SPORTS = ['Football','Cricket','Badminton','Tennis','Basketball','Hockey','Volleyball','Pickleball','Table Tennis','Squash','Athletics','Other'];
const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function ArenaxVenueAdmin() {
  const [data, setData] = useState({ enabled: false, venue: null, grounds: [], courts: [] });
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enabling, setEnabling] = useState(false);

  const [groundOpen, setGroundOpen] = useState(false);
  const [courtOpen, setCourtOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [tplsByCourt, setTplsByCourt] = useState({});

  const reload = async () => {
    try {
      const r = await api.get('/arenax/venue/me');
      setData(r.data);
      if (r.data.enabled) {
        api.get('/arenax/venue/analytics').then(rr => setAnalytics(rr.data)).catch(() => {});
        api.get('/arenax/venue/bookings').then(rr => setBookings(rr.data.bookings || [])).catch(() => {});
        api.get('/arenax/venue/slot-templates').then(rr => {
          const grouped = {};
          (rr.data.templates || []).forEach(t => {
            if (!grouped[t.court_id]) grouped[t.court_id] = [];
            grouped[t.court_id].push(t);
          });
          setTplsByCourt(grouped);
        }).catch(() => {});
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { reload(); }, []);

  const enableArenax = async () => {
    setEnabling(true);
    try {
      const r = await api.post('/arenax/venue/enable', {});
      toast.success(r.data.already_enabled ? 'ArenaX already enabled' : 'ArenaX enabled — your venue is live!');
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not enable ArenaX');
    } finally { setEnabling(false); }
  };

  if (!data.enabled) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="arenax-venue-admin-enable">
        <Card className="border-2 border-dashed border-cyan-500/30">
          <CardContent className="p-10 text-center">
            <div className="inline-flex h-14 w-14 rounded-2xl items-center justify-center mb-5"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              <Trophy className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-black mb-2">Enable ArenaX for your Academy</h1>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
              In one click, your academy becomes a bookable Venue on ArenaX — India's AI-native pay & play network.
              Existing data, RBAC, and Razorpay flows are reused. No re-onboarding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto">
              <FeatureBlurb icon={Zap} title="Bookable in minutes" desc="Configure grounds + slots. Done." />
              <FeatureBlurb icon={BarChart3} title="Realtime analytics" desc="Occupancy, revenue, payouts." />
              <FeatureBlurb icon={Sparkles} title="Player network" desc="Visible to local sports community." />
            </div>
            <Button onClick={enableArenax} disabled={enabling} data-testid="arenax-enable-button"
              className="text-base font-bold px-8 py-6"
              style={{ background: 'var(--ax-primary-gradient)', color: '#020617' }}>
              {enabling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trophy className="h-4 w-4 mr-2" />}
              Enable ArenaX
            </Button>
            <div className="mt-5 text-[11px] text-muted-foreground">8% platform commission · Cancel any time · No setup fee</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const venue = data.venue;
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="arenax-venue-admin">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Badge className="mb-2" style={{ background: 'var(--ax-primary-gradient)', color: '#020617' }}>ARENAX VENUE</Badge>
          <h1 className="text-2xl font-black flex items-center gap-2">{venue.name}</h1>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{venue.city || 'India'} · /{venue.slug}</div>
        </div>
        <div className="flex gap-2">
          <a href={`/arenax/venues/${venue.slug || venue.id}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" data-testid="arenax-view-public">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />View Public Page
            </Button>
          </a>
        </div>
      </div>

      {/* Analytics */}
      {analytics && analytics.enabled && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Paid Bookings" value={analytics.total_paid_bookings} />
          <KpiCard label="Gross Revenue" value={`₹${(analytics.revenue?.gross || 0).toFixed(0)}`} />
          <KpiCard label="Your Payout" value={`₹${(analytics.revenue?.total_revenue || 0).toFixed(0)}`} />
          <KpiCard label="Next 7d Occupancy" value={`${analytics.next_7_days_occupancy_pct}%`} />
        </div>
      )}

      {/* Grounds & Courts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Grounds & Courts</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setGroundOpen(true)} data-testid="arenax-add-ground">
              <Plus className="h-3.5 w-3.5 mr-1" />Ground
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCourtOpen(true)} disabled={!data.grounds.length} data-testid="arenax-add-court">
              <Plus className="h-3.5 w-3.5 mr-1" />Court
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {data.grounds.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">No grounds yet. Add one to start building your venue.</div>
          ) : (
            <div className="space-y-3">
              {data.grounds.map(g => (
                <div key={g.id} className="border rounded-xl p-4" data-testid={`arenax-ground-${g.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-bold text-sm">{g.name}</div>
                      <div className="text-xs text-muted-foreground">{g.sport} · {g.indoor ? 'Indoor' : 'Outdoor'} · {g.surface_type || '—'}</div>
                    </div>
                  </div>
                  {/* Courts inside this ground */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                    {data.courts.filter(c => c.ground_id === g.id).map(c => (
                      <div key={c.id} className="border rounded-lg p-3 bg-muted/30">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="font-semibold text-xs">{c.name}</div>
                          <Badge variant="secondary" className="text-[10px]">₹{c.hourly_rate}/hr</Badge>
                        </div>
                        <div className="text-[10px] text-muted-foreground mb-2">Peak: ₹{c.peak_rate}/hr</div>
                        <Button size="sm" variant="outline" className="w-full h-7 text-[11px]"
                          onClick={() => { setTplOpen({ court: c }); }}
                          data-testid={`arenax-add-template-${c.id}`}>
                          + Slot template ({(tplsByCourt[c.id] || []).length})
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Slot Generation */}
      {data.courts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" />Generate Slots</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">Generate actual bookable slot rows from your weekly templates for a date range. Idempotent — re-running skips dupes.</p>
            <Button size="sm" onClick={() => setGenOpen(true)} data-testid="arenax-generate-slots">
              <Sparkles className="h-3.5 w-3.5 mr-1" />Run Generator
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bookings */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent Bookings</CardTitle></CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <div className="text-center py-6 text-sm text-muted-foreground">No bookings yet.</div>
          ) : (
            <div className="space-y-2">
              {bookings.slice(0, 10).map(b => (
                <div key={b.id} className="flex items-center justify-between p-2.5 border rounded-lg text-sm" data-testid={`arenax-admin-booking-${b.id}`}>
                  <div>
                    <div className="font-semibold">{b.booking_code} · {b.player_name}</div>
                    <div className="text-xs text-muted-foreground">{b.sport} · {(b.slots_snapshot || []).length} slot(s)</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">₹{b.total_amount}</div>
                    <Badge variant={b.status === 'confirmed' ? 'default' : 'secondary'} className="text-[10px]">{b.status?.replace('_', ' ')}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* DIALOGS */}
      <GroundDialog open={groundOpen} onOpenChange={setGroundOpen} onCreated={reload} />
      <CourtDialog open={courtOpen} onOpenChange={setCourtOpen} grounds={data.grounds} onCreated={reload} />
      <TemplateDialog open={!!tplOpen} payload={tplOpen} onOpenChange={(v) => setTplOpen(v ? tplOpen : false)} onCreated={reload} />
      <GenerateDialog open={genOpen} onOpenChange={setGenOpen} onDone={reload} />
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="border rounded-xl p-4">
      <div className="text-[10px] tracking-wider uppercase text-muted-foreground font-semibold">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function FeatureBlurb({ icon: Icon, title, desc }) {
  return (
    <div className="border rounded-xl p-3 text-left">
      <Icon className="h-4 w-4 text-cyan-600 mb-2" />
      <div className="font-bold text-sm">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </div>
  );
}

function GroundDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: '', sport: 'Football', surface_type: '', capacity: 22, indoor: false, description: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name) { toast.error('Name required'); return; }
    setBusy(true);
    try { await api.post('/arenax/venue/grounds', f); toast.success('Ground created'); onOpenChange(false); setF({ name: '', sport: 'Football', surface_type: '', capacity: 22, indoor: false, description: '' }); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Ground</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Main turf" data-testid="arenax-ground-name" /></div>
          <div><Label>Sport</Label>
            <Select value={f.sport} onValueChange={v => setF({ ...f, sport: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SPORTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Surface type</Label><Input value={f.surface_type} onChange={e => setF({ ...f, surface_type: e.target.value })} placeholder="e.g. Artificial grass" /></div>
            <div><Label>Capacity</Label><Input type="number" value={f.capacity} onChange={e => setF({ ...f, capacity: Number(e.target.value) })} /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={f.indoor} onCheckedChange={v => setF({ ...f, indoor: v })} /><Label className="cursor-pointer">Indoor facility</Label></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="arenax-ground-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CourtDialog({ open, onOpenChange, grounds, onCreated }) {
  const [f, setF] = useState({ ground_id: '', name: '', sport: 'Football', hourly_rate: 600, peak_rate: 800, is_active: true });
  useEffect(() => { if (grounds.length && !f.ground_id) setF(s => ({ ...s, ground_id: grounds[0].id, sport: grounds[0].sport })); }, [grounds, f.ground_id]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.ground_id || !f.name) { toast.error('Pick a ground and provide a name'); return; }
    setBusy(true);
    try { await api.post('/arenax/venue/courts', f); toast.success('Court created'); onOpenChange(false); setF({ ground_id: '', name: '', sport: 'Football', hourly_rate: 600, peak_rate: 800, is_active: true }); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Court / Field</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Parent Ground</Label>
            <Select value={f.ground_id} onValueChange={v => { const g = grounds.find(x => x.id === v); setF({ ...f, ground_id: v, sport: g?.sport || f.sport }); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{grounds.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Court name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Court 1" data-testid="arenax-court-name" /></div>
          <div><Label>Sport</Label>
            <Select value={f.sport} onValueChange={v => setF({ ...f, sport: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SPORTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Hourly rate (₹)</Label><Input type="number" value={f.hourly_rate} onChange={e => setF({ ...f, hourly_rate: Number(e.target.value) })} /></div>
            <div><Label>Peak rate (₹)</Label><Input type="number" value={f.peak_rate} onChange={e => setF({ ...f, peak_rate: Number(e.target.value) })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="arenax-court-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDialog({ open, payload, onOpenChange, onCreated }) {
  const court = payload?.court;
  const [f, setF] = useState({ day_of_week: 0, start_time: '07:00', end_time: '08:00', base_price: 600, peak_price: 800, is_peak: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (court) setF(s => ({ ...s, base_price: court.hourly_rate, peak_price: court.peak_rate })); }, [court]);
  const submit = async () => {
    if (!court) return;
    setBusy(true);
    try {
      await api.post('/arenax/venue/slot-templates', { court_id: court.id, ...f });
      toast.success('Template added');
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  if (!court) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Slot Template — {court.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Day of week</Label>
            <Select value={String(f.day_of_week)} onValueChange={v => setF({ ...f, day_of_week: Number(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DAY_LABELS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start time</Label><Input type="time" value={f.start_time} onChange={e => setF({ ...f, start_time: e.target.value })} data-testid="arenax-tpl-start" /></div>
            <div><Label>End time</Label><Input type="time" value={f.end_time} onChange={e => setF({ ...f, end_time: e.target.value })} data-testid="arenax-tpl-end" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={f.is_peak} onCheckedChange={v => setF({ ...f, is_peak: v })} /><Label className="cursor-pointer">Peak slot (apply peak rate)</Label></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Base price (₹)</Label><Input type="number" value={f.base_price} onChange={e => setF({ ...f, base_price: Number(e.target.value) })} /></div>
            <div><Label>Peak price (₹)</Label><Input type="number" value={f.peak_price} onChange={e => setF({ ...f, peak_price: Number(e.target.value) })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="arenax-tpl-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateDialog({ open, onOpenChange, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const wk = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ start_date: today, end_date: wk });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/arenax/venue/slots/generate', f);
      toast.success(`${r.data.generated} slots generated across ${r.data.courts_processed} courts`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Generate Slots</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Materializes weekly templates into actual bookable slots within the date window (max 60 days).</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} data-testid="arenax-gen-start" /></div>
            <div><Label>End date</Label><Input type="date" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })} data-testid="arenax-gen-end" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={busy} data-testid="arenax-gen-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}Generate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
